import { useEffect, useRef, useState } from 'react';
import type { ColorPalette } from '@/features/colors/color.types';
import {
  BACKGROUND_INTENSITY_STEP,
  getAccentBackground,
  WASH_INTENSITY,
} from '@/features/colors/lib/paletteLookup';
import { Slider, SliderFilledTrack, SliderThumb, SliderTrack } from '@/ui';

type Props = {
  /** The saved intensity, 0-100. */
  value: number;
  isDisabled?: boolean;
  palette: ColorPalette;
  colorName: string | null | undefined;
  /** Commits the released value. */
  onChange: (value: number) => void;
  /** Renders the dragged value on the page without committing it. */
  onPreview: (value: number | null) => void;
};

/**
 * The keys that move the thumb, so releasing one can commit what it landed on. Chakra
 * fires onChangeEnd from a keyboard step only on an *uncontrolled* slider — it flags the
 * event source on a ref the controlled re-render replaces before the effect reading it
 * runs — so without this a keyboard user could preview the canvas but never save it.
 */
const COMMIT_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

/**
 * The Background section's intensity slider. Drags preview the canvas live and only the
 * release persists, so a sweep is one write rather than one per step — the same split the
 * legacy Boldness slider got from `setBoldness(v)` / `setBoldness(v, true)`.
 *
 * The track paints the ramp it selects from and the thumb carries the color at the current
 * position, so the control reads as picking a shade rather than setting a number.
 */
export const IntensitySlider = ({
  value,
  isDisabled,
  palette,
  colorName,
  onChange,
  onPreview,
}: Props) => {
  // Non-null only while dragging: `value` reads the saved schedule, which does not move
  // until the release commits, so the thumb needs somewhere else to live in between.
  const [dragValue, setDragValue] = useState<number | null>(null);
  // A ref so the release effect below doesn't re-fire on onPreview's per-render identity.
  const releasePreviewRef = useRef(onPreview);
  useEffect(() => {
    releasePreviewRef.current = onPreview;
  });
  // Both released when the saved value arrives rather than on release itself: the commit
  // and the optimistic cache write land in different renders, so letting go earlier
  // flashes the old color in between. Rolled-back writes snap both back the same way.
  useEffect(() => {
    setDragValue(null);
    releasePreviewRef.current(null);
  }, [value]);

  const current = dragValue ?? value;
  // Three stops, not two: the wash sits mid-ramp, so a two-stop gradient would put the
  // resting color somewhere the thumb never reads.
  const rampStops = [0, WASH_INTENSITY, 100].map((intensity) =>
    getAccentBackground(palette, colorName, intensity)
  );
  const thumbColor = getAccentBackground(palette, colorName, current);

  const commit = (next: number) => {
    // A no-move drag writes nothing, so the catch-up release above never fires; let go here.
    if (next === value) {
      setDragValue(null);
      onPreview(null);
      return;
    }
    // onChange releases the preview as it persists; re-arming it in the same batch keeps
    // the canvas on the new color until the saved value catches up.
    onChange(next);
    onPreview(next);
  };

  return (
    <Slider
      aria-label="Background intensity"
      flex={1}
      isDisabled={isDisabled}
      max={100}
      maxW="150px"
      min={0}
      step={BACKGROUND_INTENSITY_STEP}
      value={current}
      onChangeEnd={commit}
      onChange={(next) => {
        setDragValue(next);
        onPreview(next);
      }}
    >
      <SliderTrack
        bgGradient={`linear(to-r, ${rampStops.join(', ')})`}
        border="1px solid"
        borderColor="line.soft"
        borderRadius="ah-pill"
        h="8px"
        opacity={isDisabled ? 0.4 : 1}
        // The gradient tile defaults to the padding box but paints out to the border box,
        // so the ring under the near-transparent border fills by wrapping — the ramp's
        // darkest column lands under the left cap. Sizing the tile to the border box
        // paints the ring with the local ramp color instead.
        sx={{ backgroundOrigin: 'border-box' }}
      >
        <SliderFilledTrack bg="transparent" />
      </SliderTrack>
      <SliderThumb
        _focusVisible={{ boxShadow: 'ah-ring-active' }}
        bg={thumbColor}
        border="2px solid"
        borderColor="line.DEFAULT"
        boxSize="16px"
        onKeyUp={(event) => {
          if (dragValue !== null && COMMIT_KEYS.has(event.key)) {
            commit(dragValue);
          }
        }}
      />
    </Slider>
  );
};
